import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useVoiceConversationEngine from "components/voice/useVoiceConversationEngine";
import { invokeLLMPrompt } from "services/llmService";
import {
  releaseVoiceSession,
  hasOtherVoiceSession,
} from "./voiceSessionRegistry";
import { PAGE_GUIDE_TTS_VOICE, speakPageGuideText } from "./pageGuideAudio";

/**
 * Headless talk-back: classic voice engine + page-guide-assistant prompt.
 */
function PageGuideTalkBack({
  pageContext,
  activeStep,
  sessionId,
  onCaption,
  onError,
  onEnded,
  preferTextFallback,
  onNeedTextFallback,
}) {
  const startedRef = useRef(false);
  const endedRef = useRef(false);

  const finish = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    releaseVoiceSession(sessionId);
    onEnded?.();
  }, [onEnded, sessionId]);

  const onPauseDetected = useCallback(
    async ({ userTranscript }) => {
      const transcript = String(userTranscript || "").trim();
      if (!transcript) {
        return { question: null, isComplete: false };
      }

      onCaption?.("Let me check…");
      // Immediate ack before LLM (spoken + caption).
      await speakPageGuideText("Let me check…");

      try {
        const result = await invokeLLMPrompt({
          promptId: "page-guide-assistant",
          userMessage: JSON.stringify({
            pageContext: pageContext || "",
            stepId: activeStep?.id || null,
            nudgeText: activeStep?.nudgeText || null,
            transcript,
          }),
        });
        const reply =
          typeof result?.output === "string"
            ? result.output
            : typeof result?.content === "string"
              ? result.content
              : "";
        const spoken =
          (reply && String(reply).trim()) ||
          "Sorry — I could not answer that just now.";
        onCaption?.(spoken);
        return { question: spoken, isComplete: true };
      } catch (err) {
        const msg =
          err?.message ||
          "I could not reach the guide. You can keep working on the page.";
        onError?.(msg);
        onCaption?.(msg);
        return { question: msg, isComplete: true };
      }
    },
    [activeStep, onCaption, onError, pageContext],
  );

  const engine = useVoiceConversationEngine({
    onPauseDetected,
    openingMessage: "Go ahead — I am listening.",
    ttsProvider: "openai",
    ttsVoice: PAGE_GUIDE_TTS_VOICE,
    enableBargeIn: true,
    askDisclaimerAfterOpening: false,
    onComplete: () => finish(),
    onMicError: (msg) => {
      onError?.(msg || "Microphone unavailable.");
      if (preferTextFallback) onNeedTextFallback?.();
      else finish();
    },
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Do not acquire here — useVoiceConversationEngine holds voiceConversationEngine.
    // PageGuide idle nudges suppress when any other holder is active.
    if (hasOtherVoiceSession()) {
      onError?.("Another voice feature is active on this page.");
      finish();
      return;
    }

    if (!engine.speechSupported && preferTextFallback) {
      onNeedTextFallback?.();
      return;
    }

    onCaption?.("Go ahead — I am listening.");
    void engine.startConversation().catch((err) => {
      onError?.(err?.message || "Could not start voice guide.");
      if (preferTextFallback) onNeedTextFallback?.();
      else finish();
    });

    return () => {
      try {
        engine.stopConversation();
      } catch {
        /* ignore */
      }
    };
    // Intentionally once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // finish() still releases pageGuide if a prior nudge held it
  useEffect(() => {
    return () => {
      releaseVoiceSession(sessionId);
    };
  }, [sessionId]);

  return null;
}

PageGuideTalkBack.propTypes = {
  pageContext: PropTypes.string,
  activeStep: PropTypes.object,
  sessionId: PropTypes.string.isRequired,
  onCaption: PropTypes.func,
  onError: PropTypes.func,
  onEnded: PropTypes.func,
  preferTextFallback: PropTypes.bool,
  onNeedTextFallback: PropTypes.func,
};

export default PageGuideTalkBack;
